import { Option, OptionGroup } from '@joor/design-system'

import { OrphanRetailer } from '__generated__/atlas-types'

import { formatRetailerWithLocation } from 'formatters/orders'
import { formatDropdownOptions, formatPriceTypeIds } from 'utils/formatters'

import messages from './BrandStartOrderModal.messages'
import { formatOrphanRetailerDisplay } from './BrandStartOrderModal.utils'

export const NEW_CUSTOMER_KEY = 'new-orphan-retailer'
export const ORPHAN_CUSTOMER_KEY = 'orphan-retailer'

export interface DropdownOption {
  key: string
  value: string
  text: string
  className?: string
}

export const toDesignSystemOptions = (options: DropdownOption[]): Option[] =>
  options.map((option) => ({
    id: option.value,
    value: option.value,
    label: option.text,
  }))

export const getDesignSystemOptionId = (option: Option): string => option.value

interface ConnectedAccount {
  id: string
  displayName?: string | null
  locations?: unknown
}

interface NamedRecord {
  id: string
  name: string
  code?: string | null
}

interface GetRetailerOptionsParams {
  retailerSearchText: string
  isAddClientRestricted?: boolean
  connectedAccountsQueryLoading: boolean
  orphanRetailersQueryLoading: boolean
  connectedAccounts: ConnectedAccount[]
  orphanRetailers: OrphanRetailer[]
  quickStartOrderBetaFlag: boolean
}

export const getRetailerOptions = ({
  retailerSearchText,
  isAddClientRestricted,
  connectedAccountsQueryLoading,
  orphanRetailersQueryLoading,
  connectedAccounts,
  orphanRetailers,
  quickStartOrderBetaFlag,
}: GetRetailerOptionsParams): OptionGroup[] => {
  const retailerOptions = formatDropdownOptions(
    connectedAccounts,
    formatRetailerWithLocation,
    true,
    'notranslate',
    'id',
  )

  const orphanRetailerOptions = formatDropdownOptions(
    orphanRetailers.map((orphanRetailer) => ({
      ...orphanRetailer,
      // To determine if the option is an orphan retailer, we need to add ORPHAN_CUSTOMER_KEY to the id
      // Due to some limitations in the dropdown component.
      id: `${ORPHAN_CUSTOMER_KEY}${orphanRetailer.id}`,
    })),
    formatOrphanRetailerDisplay,
    true,
    'notranslate',
    'id',
  )

  if (connectedAccountsQueryLoading || orphanRetailersQueryLoading) {
    return [{ options: [], label: messages.connected }]
  }

  const addOrphanRetailerOption: DropdownOption = {
    key: retailerSearchText,
    value: `${NEW_CUSTOMER_KEY}${retailerSearchText}`,
    text: `${messages.create} "${retailerSearchText}"`,
  }

  return [
    {
      options: toDesignSystemOptions(retailerOptions),
      label: messages.connected,
    },
    {
      options: toDesignSystemOptions(orphanRetailerOptions),
      label: messages.unconnected,
    },
    ...(quickStartOrderBetaFlag && !isAddClientRestricted
      ? [
          {
            options: toDesignSystemOptions([addOrphanRetailerOption]),
            label: messages.addNewCustomer,
          },
        ]
      : []),
  ]
}

export const getWarehouseOptions = (warehouses: NamedRecord[]): Option[] =>
  toDesignSystemOptions(
    formatDropdownOptions(warehouses, undefined, false, 'notranslate'),
  )

export const getOrderTypeOptions = (orderTypes: NamedRecord[]): Option[] =>
  toDesignSystemOptions(
    formatDropdownOptions(orderTypes, undefined, false, 'notranslate'),
  )

export const getPriceTypeOptions = (priceTypes: NamedRecord[]): Option[] =>
  toDesignSystemOptions(
    formatDropdownOptions(
      formatPriceTypeIds(priceTypes),
      undefined,
      false,
      'notranslate',
    ),
  )

export const getOrderEventOptions = (orderEvents: NamedRecord[]): Option[] =>
  toDesignSystemOptions(
    formatDropdownOptions(orderEvents, undefined, false, 'notranslate'),
  )

export const getCollectionOptions = (collections: DropdownOption[]): Option[] =>
  toDesignSystemOptions(collections)

export const flattenOptionGroups = (optionGroups: OptionGroup[]): Option[] =>
  optionGroups.flatMap((optionGroup) => optionGroup.options)
